// ABOUTME: Orchestration hook for the Unloading Methods entity — combines config query, mutations, and form state.
// ABOUTME: Returns a uniform interface consumed by CountryConfigurationsEntity to keep the page component thin.

import { useCallback, useMemo, useRef } from 'react'
import { useUnloadingMethodsConfig } from './useUnloadingMethodsConfig'
import {
  useCreateUnloadingMethods,
  useCreateUnloadingMethodsWithScope,
  useUpdateUnloadingMethods,
  useDeleteUnloadingMethods,
} from './useUnloadingMethodsMutations'
import { UnloadingMethodsConfig, UnloadingMethodsFormValues } from '../types/unloadingMethods'
import type { EntityActionResult, PendingScopeRequest } from '../context/EntityFormContext'
import { getConfigScopeId, toPendingScopeConfig } from '../utils/pendingScope'

interface UseUnloadingMethodsEntityParams {
  countryId: string | undefined
  businessLine: string | undefined
  scopeId: string | undefined
  pendingScope?: PendingScopeRequest | undefined
  rootScopeId?: string | undefined
}

interface EntityActions {
  handleSave: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleReset: (onSuccess: () => void, onError?: (error?: unknown) => void) => void
  handleValuesChange: (values: UnloadingMethodsFormValues) => void
}

interface UseUnloadingMethodsEntityResult {
  config: UnloadingMethodsConfig | undefined
  isLoading: boolean
  error: Error | null
  isSaving: boolean
  actions: EntityActions
}

export const useUnloadingMethodsEntity = ({
  countryId,
  businessLine,
  scopeId,
  pendingScope,
  rootScopeId,
}: UseUnloadingMethodsEntityParams): UseUnloadingMethodsEntityResult => {
  const configScopeId = getConfigScopeId(scopeId, pendingScope, rootScopeId)
  const { data: rawConfig, isLoading, error } = useUnloadingMethodsConfig(
    countryId,
    businessLine,
    configScopeId,
    rootScopeId
  )
  const config = useMemo(() => toPendingScopeConfig(rawConfig, pendingScope, (cfg) => cfg.values), [rawConfig, pendingScope])

  const createMutation = useCreateUnloadingMethods(countryId, businessLine)
  const createWithScopeMutation = useCreateUnloadingMethodsWithScope(countryId, businessLine)
  const updateMutation = useUpdateUnloadingMethods(countryId, businessLine)
  const deleteMutation = useDeleteUnloadingMethods(countryId, businessLine)

  const formValuesRef = useRef<UnloadingMethodsFormValues | null>(null)
  const pendingScopeRef = useRef<PendingScopeRequest | undefined>(pendingScope)

  pendingScopeRef.current = pendingScope

  const handleValuesChange = useCallback((values: UnloadingMethodsFormValues) => {
    formValuesRef.current = values
  }, [])

  const handleSave = useCallback((onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
    if (!formValuesRef.current) return
    const values = formValuesRef.current
    const scopeToCreate = pendingScopeRef.current
    if (scopeToCreate) {
      createWithScopeMutation.mutate({ scope: scopeToCreate, values }, { onSuccess: (result) => onSuccess({ wasOverriding: true, createdScopeId: result?.scopeId }), onError: (error) => onError?.(error) })
      return
    }
    if (!scopeId) return
    const isNew = !config?.id
    const mutate = isNew ? createMutation.mutate : updateMutation.mutate
    mutate({ scopeId, values }, { onSuccess: (result) => onSuccess({ wasOverriding: true, createdScopeId: result?.scopeId }), onError: (error) => onError?.(error) })
  }, [scopeId, config, createMutation.mutate, createWithScopeMutation.mutate, updateMutation.mutate])

  const handleReset = useCallback((onSuccess: () => void, onError?: (error?: unknown) => void) => {
    if (!scopeId) return
    deleteMutation.mutate(scopeId, { onSuccess, onError: (error) => onError?.(error) })
  }, [scopeId, deleteMutation.mutate])

  const actions = useMemo(
    () => ({ handleSave, handleReset, handleValuesChange }),
    [handleSave, handleReset, handleValuesChange]
  )

  const isSaving = createMutation.isPending || createWithScopeMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  return {
    config,
    isLoading,
    error,
    isSaving,
    actions,
  }
}
